import { valibotResolver } from "@hookform/resolvers/valibot";
import { useState } from "react";
import { useForm } from "react-hook-form";

import EnterPin from "common/components/LinkedInLogin/PasswordLogin/EnterPin";
import LoginForm from "common/components/LinkedInLogin/PasswordLogin/LoginForm";
import ThisWasMeChallenge from "common/components/LinkedInLogin/PasswordLogin/ThisWasMeChallenge";
import useGTM from "common/hooks/useGTM";
import { LoginSchema, type LoginSchemaType } from "common/schemas";
import type { WorkspaceAccount } from "common/types";

export interface PasswordLoginProps {
  onSuccess: () => void;
  selectedProxy: string;
  setSelectedProxy: (newProxy: string) => void;
  setCurrentAction: (currentAction: "selectMethod" | "addProxy") => void;
  account?: WorkspaceAccount;
}

type CurrentStep = "login" | "pin" | "thiswasme";

export default function PasswordLogin({
  onSuccess,
  selectedProxy,
  setSelectedProxy,
  setCurrentAction,
  account = null,
}: PasswordLoginProps) {
  const { sendEvent } = useGTM();
  const [currentStep, setCurrentStep] = useState<CurrentStep>("login");

  const formReturn = useForm<LoginSchemaType>({
    defaultValues: {
      email: account?.email ?? "",
      password: "",
    },
    resolver: valibotResolver(LoginSchema),
  });

  function onAppChallengeFail(): void {
    setCurrentStep("login");
    formReturn.setError("password", {
      message: "Error resolving app challenge",
    });
  }

  function onLoginSuccess(): void {
    sendEvent("connect_linkedin_account_success");
    onSuccess();
  }

  const steps = {
    login: {
      title: `${account ? "Re-connect" : "Connect"} your LinkedIn account`,
      subtitle: "",
      mainComponent: (
        <LoginForm
          account={account}
          onSuccess={onLoginSuccess}
          formReturn={formReturn}
          setStep={setCurrentStep}
          setCurrentAction={setCurrentAction}
          selectedProxy={selectedProxy}
          setSelectedProxy={setSelectedProxy}
        />
      ),
    },
    pin: {
      title: "Enter your PIN",
      subtitle:
        "You received a pin from LinkedIn via SMS, email or authenticator app. Please enter it below to connect your LinkedIn account",
      mainComponent: (
        <EnterPin
          email={formReturn.getValues("email")}
          onSuccess={onLoginSuccess}
        />
      ),
    },
    thiswasme: {
      title: "Confirm your login",
      subtitle:
        "LinkedIn wants to make sure this is an authorized login to your account. Please go to your LinkedIn app and confirm this connection",
      mainComponent: (
        <ThisWasMeChallenge
          email={formReturn.getValues("email")}
          onSuccess={onLoginSuccess}
          onAppChallengeFail={onAppChallengeFail}
        />
      ),
    },
  };

  const { title, subtitle, mainComponent } = steps[currentStep];

  return (
    <>
      <h2 className="text-center text-2xl-bold lg:text-xl-bold">{title}</h2>
      {subtitle && (
        <p className="mt-2 text-center text-sm text-black-500">{subtitle}</p>
      )}
      <div className="mt-8 flex flex-col">{mainComponent}</div>
    </>
  );
}
